import { useQuery, UseQueryOptions } from "@tanstack/react-query";

export type AsyncAction<T> = () => Promise<T>;

export type ActionKeyPair<TParam, TResult> = {
  key: string;
  actionBuilder: (param: TParam) => AsyncAction<TResult>;
};

export interface IService {
  [key: string]: ActionKeyPair<unknown, unknown> | unknown;
}

type SelectAction<TService extends IService, TParam, TResult> = (
  service: TService
) => ActionKeyPair<TParam, TResult>;

type UseCustomQueryProps<TService extends IService, TParam, TResult> = {
  serviceInstance: TService;
  selectAction: SelectAction<TService, TParam, TResult>;
  parameter: TParam;
  options?: Omit<UseQueryOptions<TResult>, "queryKey" | "queryFn">;
};

export function useCustomQuery<TService extends IService, TParam, TResult>({
  serviceInstance,
  selectAction,
  parameter,
  options,
}: UseCustomQueryProps<TService, TParam, TResult>) {
  const { key, actionBuilder } = selectAction(serviceInstance);

  return useQuery({
    queryKey: [key, parameter],
    queryFn: actionBuilder(parameter),
    ...options,
  });
}
